import { useEffect, useState } from 'react';

import { readOauthCallback, readOauthError } from '../api/oauthCallback';
import { TokenStore } from '../api/tokenStore';
import { AppText } from '../components/AppText';
import { Strings } from '../strings';

/**
 * Where a provider sign-in lands.
 *
 * **Not a screen, and deliberately not in `screens/`.** It renders nothing a person is meant to look at —
 * it reads the fragment and leaves. The three clients' screen inventories are compared for parity, and a
 * landing that exists only on the web would read as drift when it is not.
 *
 * The API redirected here with the token pair in the fragment. This stores it and sends the browser on —
 * with `location.replace`, so the address holding a live refresh token does not stay in history where the
 * back button, or the next person to use the device, could return to it (R0148).
 */
export function OauthLanding() {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const tokens = readOauthCallback(window.location.hash);

    if (tokens === null) {
      // A declined consent screen arrives here too, and it is not a fault worth alarming anybody about —
      // but there is nothing to do either way except offer the sign-in screen again.
      setFailed(readOauthError(window.location.hash) === null);
      window.location.replace('/sign-in');

      return;
    }

    TokenStore.writeParentTokens(tokens.accessToken, tokens.refreshToken);
    // The fragment is cleared by navigating away from it rather than by rewriting it in place, so no
    // intermediate state exists where the tokens are stored *and* still in the address bar.
    // A full load rather than a route change: the shell reads the token store once, on start, so it has
    // to start again to see the session that has just been written.
    window.location.replace('/');
  }, []);

  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--space-5)' }}>
      <AppText color="var(--text-secondary)">
        {failed ? Strings.signInProviderFailed : Strings.appName}
      </AppText>
    </div>
  );
}
